import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Leaderboard as LeaderboardIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
  Task as TaskIcon,
  VpnKey as TokenIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';

const API_BASE = '/api';
const TOKEN_KEY = 'relationship_token';

const text = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const num = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
};

const arr = <T,>(value: unknown) => (Array.isArray(value) ? (value as T[]) : []);
const normalizeToken = (value: string) => value.replace(/^Bearer\s+/i, '').trim();
const getStoredToken = () => normalizeToken(localStorage.getItem(TOKEN_KEY) || '');
const isMobileClient = () => /Android|iPhone|iPad|iPod|Mobile/i.test(window.navigator.userAgent || '');
const buildWechatRedirectUrl = () => {
  const url = new URL(window.location.href);
  ['code', 'state', 'token'].forEach((key) => url.searchParams.delete(key));
  return url.toString();
};

async function api<T>(endpoint: string, options: RequestInit = {}, tokenOverride?: string): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.body) headers.set('Content-Type', 'application/json');
  const token = normalizeToken(tokenOverride ?? getStoredToken());
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  let payload: any = null;
  try {
    payload = await response.json();
  } catch {}

  if (!response.ok) throw new Error(text(payload?.error, payload?.message) || `请求失败（${response.status}）`);
  if (payload && typeof payload === 'object' && payload.ok === false) {
    throw new Error(text(payload.error, payload.message) || '接口返回失败');
  }
  return (payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload) as T;
}

const taskStatus = (value: unknown) => {
  const status = text(value).toLowerCase();
  if (['completed', 'done', 'finished'].includes(status)) return 'completed';
  if (['active', 'in_progress', 'ongoing'].includes(status)) return 'active';
  return 'pending';
};

const taskLabelMap: Record<string, string> = {
  housework: '家务',
  study: '学习',
  work: '工作',
  exercise: '运动',
  other: '其他',
};

const groupLabelMap: Record<string, string> = {
  family: '家庭',
  couple: '情侣',
  friends: '朋友',
  roommates: '室友',
  teacher_student: '师生',
};

export default function App() {
  const [user, setUser] = useState<any | null>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [points, setPoints] = useState<any>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [initializing, setInitializing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'info'; text: string } | null>(null);
  const [loginToken, setLoginToken] = useState(getStoredToken());
  const [loginCode, setLoginCode] = useState('');
  const [wechatRedirectHandled, setWechatRedirectHandled] = useState(false);
  const [wechatQrDialogOpen, setWechatQrDialogOpen] = useState(false);
  const [wechatQrUrl, setWechatQrUrl] = useState('');
  const [wechatQrState, setWechatQrState] = useState('');
  const [wechatQrImage, setWechatQrImage] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [groupDraft, setGroupDraft] = useState({ name: '', type: 'family', description: '' });
  const [taskDraft, setTaskDraft] = useState({ title: '', description: '', category: 'housework', points: 5, receiverId: '' });
  const [taskKeyword, setTaskKeyword] = useState('');
  const [taskFilter, setTaskFilter] = useState('all');

  const loadUser = useCallback(async (tokenOverride?: string) => {
    const data = await api<any>('/users/me', {}, tokenOverride);
    const nextUser = data?.user ?? data;
    setUser({
      id: text(nextUser?.userId, nextUser?.id),
      nickname: text(nextUser?.nickname, nextUser?.name) || '未命名用户',
      avatar: text(nextUser?.avatarUrl, nextUser?.avatar),
      pointsTotal: num(nextUser?.pointsTotal, nextUser?.totalPoints, nextUser?.points),
    });
  }, []);

  const loadAll = useCallback(async (tokenOverride?: string) => {
    const results = await Promise.allSettled([
      loadUser(tokenOverride),
      api<any>('/groups/my').then((data) => {
        setGroups(arr<any>(data?.groups ?? data).map((item) => ({
          id: text(item?.groupId, item?.id),
          name: text(item?.groupName, item?.name) || '未命名小组',
          type: text(item?.groupType, item?.type) || 'family',
          memberCount: num(item?.memberCount, item?.size, 1),
          points: num(item?.myPoints, item?.points),
          role: text(item?.myRole, item?.role) || '成员',
          description: text(item?.description),
        })));
      }),
      api<any>('/tasks').then((data) => {
        setTasks(arr<any>(data?.tasks ?? data).map((item) => ({
          id: text(item?.taskId, item?.id),
          title: text(item?.title) || '未命名任务',
          description: text(item?.description),
          category: text(item?.category) || 'other',
          points: num(item?.points, item?.score, 5),
          status: taskStatus(item?.status),
        })));
      }),
      api<any>('/points/summary').then((data) => {
        setPoints(data);
        setUser((previous: any) =>
          previous
            ? { ...previous, pointsTotal: num(data?.totalPoints, data?.pointsTotal, data?.currentPoints, previous.pointsTotal) }
            : previous
        );
      }),
      api<any>('/leaderboard').then((data) => {
        setLeaderboard(arr<any>(data?.leaderboard ?? data).map((item) => ({
          id: text(item?.userId, item?.id),
          nickname: text(item?.nickname, item?.name) || '匿名用户',
          title: text(item?.currentTitle, item?.title) || '成员',
          totalPoints: num(item?.totalPoints, item?.points),
        })));
      }),
    ]);

    if (results[0].status === 'rejected') {
      throw results[0].reason;
    }

    return results.filter((item) => item.status === 'rejected').length;
  }, [loadUser]);

  useEffect(() => {
    const restore = async () => {
      const token = getStoredToken();
      if (!token) {
        setInitializing(false);
        return;
      }
      try {
        await loadAll(token);
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY);
        setMessage({ type: 'error', text: error instanceof Error ? error.message : '会话恢复失败' });
      } finally {
        setInitializing(false);
      }
    };
    void restore();
  }, [loadAll]);

  const finishWechatCodeLogin = useCallback(async (code: string, stateOverride?: string) => {
    const data = await api<any>('/auth/wechat-login', {
      method: 'POST',
      body: JSON.stringify({
        code: code.trim(),
        state: text(stateOverride),
      }),
    });
    const token = normalizeToken(text(data?.token, data?.accessToken, data?.sessionToken));
    if (!token) throw new Error('登录成功但接口未返回 token');
    localStorage.setItem(TOKEN_KEY, token);
    setLoginToken(token);
    setLoginCode('');
    setWechatQrDialogOpen(false);
    setWechatQrUrl('');
    setWechatQrState('');
    setWechatQrImage('');
    const failedCount = await loadAll(token);
    setMessage(
      failedCount > 0
        ? { type: 'info', text: `微信登录成功，但有 ${failedCount} 个模块刷新失败` }
        : { type: 'success', text: data?.linkedToPc ? '微信登录成功，PC 端已同步登录' : '微信登录成功' }
    );
  }, [loadAll]);

  useEffect(() => {
    if (!wechatQrUrl) {
      setWechatQrImage('');
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(wechatQrUrl, { width: 280, margin: 1 })
      .then((image) => {
        if (!cancelled) setWechatQrImage(image);
      })
      .catch(() => {
        if (!cancelled) setWechatQrImage('');
      });
    return () => {
      cancelled = true;
    };
  }, [wechatQrUrl]);

  useEffect(() => {
    if (!wechatQrDialogOpen || !wechatQrState || user) return;
    let cancelled = false;
    let timer: number | null = null;

    const resetPcQrLogin = () => {
      setWechatQrDialogOpen(false);
      setWechatQrUrl('');
      setWechatQrState('');
      setWechatQrImage('');
    };

    const poll = async () => {
      try {
        const data = await api<any>(`/auth/wechat-status?state=${encodeURIComponent(wechatQrState)}`);
        const status = text(data?.status).toLowerCase();
        if (status === 'authorized') {
          const token = normalizeToken(text(data?.token, data?.accessToken, data?.sessionToken));
          if (!token) throw new Error('PC 登录已授权，但 token 丢失');
          localStorage.setItem(TOKEN_KEY, token);
          if (!cancelled) {
            setLoginToken(token);
            setBusy(true);
            resetPcQrLogin();
            const failedCount = await loadAll(token);
            setMessage(
              failedCount > 0
                ? { type: 'info', text: `PC 扫码登录成功，但有 ${failedCount} 个模块刷新失败` }
                : { type: 'success', text: 'PC 扫码登录成功' }
            );
          }
        } else if (status === 'expired') {
          if (!cancelled) {
            resetPcQrLogin();
            setMessage({ type: 'error', text: '二维码已过期，请重新点击微信登录' });
          }
        }
      } catch (error) {
        if (!cancelled) {
          resetPcQrLogin();
          setMessage({ type: 'error', text: error instanceof Error ? error.message : '轮询微信登录状态失败' });
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    };

    void poll();
    timer = window.setInterval(() => {
      void poll();
    }, 2000);

    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [loadAll, user, wechatQrDialogOpen, wechatQrState]);

  useEffect(() => {
    if (user || wechatRedirectHandled) return;
    const currentUrl = new URL(window.location.href);
    const token = normalizeToken(currentUrl.searchParams.get('token') || '');
    const code = (currentUrl.searchParams.get('code') || '').trim();
    const state = (currentUrl.searchParams.get('state') || '').trim();

    if (!token && !code) {
      setWechatRedirectHandled(true);
      return;
    }

    setWechatRedirectHandled(true);
    setBusy(true);
    setMessage({ type: 'info', text: '微信授权中，正在登录...' });

    const clearWechatParams = () => {
      currentUrl.searchParams.delete('token');
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('state');
      window.history.replaceState({}, document.title, currentUrl.toString());
    };

    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      void loadAll(token)
        .then((failedCount) => {
          setMessage(
            failedCount > 0
              ? { type: 'info', text: `微信登录成功，但有 ${failedCount} 个模块刷新失败` }
              : { type: 'success', text: '微信登录成功' }
          );
          clearWechatParams();
        })
        .catch((error) => {
          localStorage.removeItem(TOKEN_KEY);
          setMessage({ type: 'error', text: error instanceof Error ? error.message : '微信登录失败' });
        })
        .finally(() => setBusy(false));
      return;
    }

    void finishWechatCodeLogin(code, state)
      .then(() => {
        clearWechatParams();
      })
      .catch((error) => {
        localStorage.removeItem(TOKEN_KEY);
        setMessage({ type: 'error', text: error instanceof Error ? error.message : '微信登录失败' });
      })
      .finally(() => setBusy(false));
  }, [finishWechatCodeLogin, loadAll, user, wechatRedirectHandled]);

  const refresh = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const failedCount = await loadAll();
      setMessage(
        failedCount > 0
          ? { type: 'info', text: `刷新完成，但有 ${failedCount} 个模块刷新失败` }
          : { type: 'success', text: '刷新成功' }
      );
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '刷新失败' });
    } finally {
      setBusy(false);
    }
  };

  const loginByToken = async () => {
    const token = normalizeToken(loginToken);
    if (!token) {
      setMessage({ type: 'error', text: '请输入有效 Token' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      localStorage.setItem(TOKEN_KEY, token);
      const failedCount = await loadAll(token);
      setMessage(
        failedCount > 0
          ? { type: 'info', text: `Token 登录成功，但有 ${failedCount} 个模块刷新失败` }
          : { type: 'success', text: 'Token 登录成功' }
      );
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Token 登录失败' });
    } finally {
      setBusy(false);
    }
  };

  const startWechatLogin = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const redirectUri = encodeURIComponent(buildWechatRedirectUrl());
      const mode = isMobileClient() ? 'mobile' : 'pc';
      const data = await api<any>(`/auth/wechat-url?redirectUri=${redirectUri}&mode=${mode}`);
      const authUrl = text(data?.url, data?.authUrl, data?.loginUrl, data?.qrUrl);
      if (!authUrl) throw new Error('后端未返回微信授权地址');
      if (mode === 'mobile') {
        window.location.href = authUrl;
        return;
      }
      setWechatQrUrl(authUrl);
      setWechatQrState(text(data?.state));
      setWechatQrDialogOpen(true);
      setMessage({ type: 'info', text: '请用微信扫码确认登录，PC 页面会自动完成登录。' });
      setBusy(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error
          ? `${error.message}，请改用下方 Code 登录`
          : '微信跳转失败，请改用 Code 登录',
      });
      setBusy(false);
    }
  };

  const loginByCode = async () => {
    if (!loginCode.trim()) {
      setMessage({ type: 'error', text: '请输入微信授权 code' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await finishWechatCodeLogin(loginCode);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '微信登录失败' });
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setGroups([]);
    setTasks([]);
    setPoints(null);
    setLeaderboard([]);
    setLoginToken('');
    setWechatQrDialogOpen(false);
    setWechatQrUrl('');
    setWechatQrState('');
    setWechatQrImage('');
    setMessage({ type: 'info', text: '已退出登录' });
  };

  const createGroup = async () => {
    if (!groupDraft.name.trim()) {
      setMessage({ type: 'error', text: '请输入小组名称' });
      return;
    }
    setBusy(true);
    try {
      await api('/groups', {
        method: 'POST',
        body: JSON.stringify({
          groupName: groupDraft.name.trim(),
          groupType: groupDraft.type,
          description: groupDraft.description.trim(),
        }),
      });
      setGroupDialogOpen(false);
      setGroupDraft({ name: '', type: 'family', description: '' });
      await loadAll();
      setMessage({ type: 'success', text: '小组创建成功' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '创建小组失败' });
    } finally {
      setBusy(false);
    }
  };

  const createTask = async () => {
    if (!taskDraft.title.trim()) {
      setMessage({ type: 'error', text: '请输入任务标题' });
      return;
    }
    setBusy(true);
    try {
      await api('/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: taskDraft.title.trim(),
          description: taskDraft.description.trim(),
          category: taskDraft.category,
          points: Math.max(1, num(taskDraft.points, 5)),
          receiverId: taskDraft.receiverId.trim() || user?.id || '',
        }),
      });
      setTaskDialogOpen(false);
      setTaskDraft({ title: '', description: '', category: 'housework', points: 5, receiverId: '' });
      await loadAll();
      setMessage({ type: 'success', text: '任务创建成功' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '创建任务失败' });
    } finally {
      setBusy(false);
    }
  };

  const completeTask = async (taskId: string) => {
    setBusy(true);
    try {
      const data = await api<any>(`/tasks/${taskId}/complete`, { method: 'POST' });
      await loadAll();
      setMessage({ type: 'success', text: `任务已完成，获得 ${num(data?.pointsEarned, data?.points, 0)} 积分` });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '完成任务失败' });
    } finally {
      setBusy(false);
    }
  };

  const filteredTasks = useMemo(() => {
    const keyword = taskKeyword.trim().toLowerCase();
    return tasks.filter((task) => {
      if (taskFilter !== 'all' && task.status !== taskFilter) return false;
      if (!keyword) return true;
      return `${task.title} ${task.description}`.toLowerCase().includes(keyword);
    });
  }, [taskFilter, taskKeyword, tasks]);

  const metrics = useMemo(() => {
    const totalPoints = num(points?.totalPoints, points?.pointsTotal, points?.currentPoints, user?.pointsTotal);
    return [
      { label: '总积分', value: totalPoints, hint: '当前累计贡献' },
      { label: '本周新增', value: num(points?.weeklyPoints, points?.weekPoints, points?.thisWeekPoints), hint: '最近 7 天' },
      { label: '本月新增', value: num(points?.monthlyPoints, points?.monthPoints, points?.thisMonthPoints), hint: '最近 30 天' },
      { label: '当前排名', value: num(points?.rank, points?.myRank, leaderboard.findIndex((item) => item.id === user?.id) + 1), hint: '排行榜位置' },
    ];
  }, [leaderboard, points, user?.id, user?.pointsTotal]);

  if (initializing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">正在恢复登录状态...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', px: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card sx={{ width: '100%', maxWidth: 460 }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h4" align="center" gutterBottom>暖暖约定</Typography>
                <Typography variant="body2" color="text.secondary" align="center">先真实登录，再进入任务与积分面板。</Typography>
              </Box>
              {message && <Alert severity={message.type}>{message.text}</Alert>}
              <TextField label="访问令牌" value={loginToken} onChange={(e) => setLoginToken(e.target.value)} placeholder="粘贴后端签发的 token" fullWidth />
              <Button variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <TokenIcon />} onClick={loginByToken} disabled={busy}>使用 Token 登录</Button>
              <Button variant="contained" color="success" onClick={startWechatLogin} disabled={busy}>
                微信登录（手机直跳 / PC 扫码）
              </Button>
              <TextField label="微信授权 Code" value={loginCode} onChange={(e) => setLoginCode(e.target.value)} placeholder="粘贴真实授权 code" fullWidth />
              <Button variant="outlined" onClick={loginByCode} disabled={busy}>使用 Code 登录</Button>
            </Stack>
          </CardContent>
        </Card>
        <Dialog
          open={wechatQrDialogOpen}
          onClose={() => {
            if (busy) return;
            setWechatQrDialogOpen(false);
            setWechatQrUrl('');
            setWechatQrState('');
            setWechatQrImage('');
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>微信扫码登录</DialogTitle>
          <DialogContent>
            <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                请用手机微信扫码，确认授权后当前 PC 页面会自动登录。
              </Typography>
              {wechatQrImage ? (
                <Box component="img" src={wechatQrImage} alt="微信登录二维码" sx={{ width: 280, maxWidth: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
              ) : (
                <Box sx={{ width: 280, height: 280, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
              <TextField
                label="授权链接"
                value={wechatQrUrl}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="如果扫码失败，也可以把这个链接发到手机微信里打开。"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setWechatQrDialogOpen(false);
                setWechatQrUrl('');
                setWechatQrState('');
                setWechatQrImage('');
              }}
            >
              关闭
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>暖暖约定</Typography>
          <Button color="inherit" startIcon={<RefreshIcon />} onClick={refresh} disabled={busy}>刷新</Button>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar src={user.avatar}>{user.nickname.charAt(0)}</Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2">{user.nickname}</Typography>
              <Typography variant="caption">积分 {user.pointsTotal}</Typography>
            </Box>
          </Stack>
          <IconButton color="inherit" onClick={logout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'primary.main' }}><GroupIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">小组数</Typography><Typography variant="h5">{groups.length}</Typography></Box></Stack></CardContent></Card></Grid>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'warning.main' }}><TaskIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">待处理任务</Typography><Typography variant="h5">{tasks.filter((item) => item.status !== 'completed').length}</Typography></Box></Stack></CardContent></Card></Grid>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'success.main' }}><CheckCircleIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">已完成任务</Typography><Typography variant="h5">{tasks.filter((item) => item.status === 'completed').length}</Typography></Box></Stack></CardContent></Card></Grid>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'secondary.main' }}><LeaderboardIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">总积分</Typography><Typography variant="h5">{num(points?.totalPoints, points?.pointsTotal, user.pointsTotal)}</Typography></Box></Stack></CardContent></Card></Grid>
        </Grid>

        <Paper sx={{ mt: 3, p: 1 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
            <Tab label="小组" />
            <Tab label="任务" />
            <Tab label="积分" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <Box sx={{ mt: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
              <Box><Typography variant="h5">我的小组</Typography><Typography variant="body2" color="text.secondary">已支持真实创建和刷新。</Typography></Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGroupDialogOpen(true)}>创建小组</Button>
            </Stack>
            {groups.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6" gutterBottom>暂无小组</Typography><Button variant="contained" onClick={() => setGroupDialogOpen(true)}>立即创建</Button></Paper>
            ) : (
              <Grid container spacing={3}>
                {groups.map((group) => (
                  <Grid item xs={12} md={6} key={group.id || group.name}>
                    <Card className="shadow-card">
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'primary.main' }}><GroupIcon /></Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6">{group.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{groupLabelMap[group.type] || group.type} · {group.memberCount} 人 · {group.role}</Typography>
                            </Box>
                            <Chip label={`${group.points} 积分`} color="primary" />
                          </Stack>
                          {group.description && <Typography variant="body2" color="text.secondary">{group.description}</Typography>}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ mt: 3 }}>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
                <Box><Typography variant="h5">任务管理</Typography><Typography variant="body2" color="text.secondary">已支持创建、筛选、完成，并联动积分刷新。</Typography></Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTaskDialogOpen(true)}>新建任务</Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField fullWidth label="搜索任务" value={taskKeyword} onChange={(e) => setTaskKeyword(e.target.value)} /></Grid>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="状态筛选" value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)}>
                    <MenuItem value="all">全部状态</MenuItem>
                    <MenuItem value="pending">待处理</MenuItem>
                    <MenuItem value="active">进行中</MenuItem>
                    <MenuItem value="completed">已完成</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Stack>
            {filteredTasks.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6" gutterBottom>暂无匹配任务</Typography><Button variant="contained" onClick={() => setTaskDialogOpen(true)}>创建任务</Button></Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredTasks.map((task) => (
                  <Grid item xs={12} key={task.id || task.title}>
                    <Card className="shadow-card">
                      <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6">{task.title}</Typography>
                            {task.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{task.description}</Typography>}
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                              <Chip label={taskLabelMap[task.category] || task.category} size="small" />
                              <Chip label={`${task.points} 积分`} size="small" color="primary" variant="outlined" />
                              <Chip label={task.status === 'completed' ? '已完成' : task.status === 'active' ? '进行中' : '待处理'} size="small" color={task.status === 'completed' ? 'success' : task.status === 'active' ? 'warning' : 'default'} variant="outlined" />
                            </Stack>
                          </Box>
                          {task.status !== 'completed' && <Button variant="contained" onClick={() => completeTask(task.id)} disabled={busy}>标记完成</Button>}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>积分面板</Typography>
            <Grid container spacing={2}>
              {metrics.map((metric) => (
                <Grid item xs={12} sm={6} md={3} key={metric.label}>
                  <Card><CardContent><Typography variant="body2" color="text.secondary">{metric.label}</Typography><Typography variant="h4" sx={{ my: 1 }}>{metric.value > 0 || metric.label === '当前排名' ? metric.label === '当前排名' ? `#${metric.value || '--'}` : metric.value : 0}</Typography><Typography variant="caption" color="text.secondary">{metric.hint}</Typography></CardContent></Card>
                </Grid>
              ))}
            </Grid>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>排行榜</Typography>
                {leaderboard.length === 0 ? (
                  <Typography color="text.secondary">暂无排行榜数据</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {leaderboard.map((item, index) => (
                      <Paper key={item.id || item.nickname} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Chip label={`#${index + 1}`} color={index === 0 ? 'primary' : 'default'} size="small" />
                          <Avatar>{item.nickname.charAt(0)}</Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1">{item.nickname}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.title}</Typography>
                          </Box>
                          <Typography variant="subtitle1">{item.totalPoints}</Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Container>

      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>创建小组</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus label="小组名称" value={groupDraft.name} onChange={(e) => setGroupDraft({ ...groupDraft, name: e.target.value })} fullWidth />
            <TextField select label="小组类型" value={groupDraft.type} onChange={(e) => setGroupDraft({ ...groupDraft, type: e.target.value })} fullWidth>
              {Object.entries(groupLabelMap).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="描述" value={groupDraft.description} onChange={(e) => setGroupDraft({ ...groupDraft, description: e.target.value })} multiline minRows={3} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>取消</Button>
          <Button onClick={createGroup} variant="contained" disabled={busy}>创建</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>创建任务</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus label="任务标题" value={taskDraft.title} onChange={(e) => setTaskDraft({ ...taskDraft, title: e.target.value })} fullWidth />
            <TextField label="任务描述" value={taskDraft.description} onChange={(e) => setTaskDraft({ ...taskDraft, description: e.target.value })} multiline minRows={3} fullWidth />
            <TextField select label="任务分类" value={taskDraft.category} onChange={(e) => setTaskDraft({ ...taskDraft, category: e.target.value })} fullWidth>
              {Object.entries(taskLabelMap).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="积分" type="number" value={taskDraft.points} onChange={(e) => setTaskDraft({ ...taskDraft, points: Math.max(1, Number(e.target.value) || 1) })} fullWidth />
            <TextField label="接收人 ID（可选）" value={taskDraft.receiverId} onChange={(e) => setTaskDraft({ ...taskDraft, receiverId: e.target.value })} helperText="留空时默认使用当前登录用户" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>取消</Button>
          <Button onClick={createTask} variant="contained" disabled={busy}>创建</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


